import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { URLS, Url } from '@wix/da-url';
import { PapiUser, PapiPremiumFolderData } from '@wix/da-types-papi';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-shared-react/pkg/Icons/EyeDisc2';
import PremiumFolderDisc from '@wix/da-shared-react/pkg/Icons/PremiumFolderDisc';
import PointsIcon from '@wix/da-shared-react/pkg/Icons/Points';
import Button from '@wix/da-shared-react/pkg/Button';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import CoreIconSmall from '@wix/da-shared-react/pkg/Icons/CoreIconSmall';
import BasePaywall from '../BasePaywall';
import s from './FolderPaywall.scss';

export interface Props {
  premiumFolderData?: NonNullable<PapiPremiumFolderData>;
  contentOwner: PapiUser;
  showPremiumFolderPopup: (folderId: number) => void;
  folderId?: number;
  biData?: any;
  children: React.ReactNode;
}

const FolderPaywall: React.FC<Props> = ({
  contentOwner,
  premiumFolderData,
  showPremiumFolderPopup,
  folderId,
  biData,
  children,
}) => {
  const { t } = useTranslation();
  const newCheckout = useFlag('new_checkout');
  const {
    type,
    hasAccess,
    pointsPrice,
    dollarPrice,
    galleryUrl,
    subproductId,
  } = premiumFolderData || {};
  const onShowPopup = useCallback(() => {
    if (folderId) {
      showPremiumFolderPopup(folderId);
    }
  }, [folderId, showPremiumFolderPopup]);

  return (
    <>
      {type === 'paid' && (
        <BasePaywall
          hasAccess={!!hasAccess}
          icon={<PremiumFolderDisc size={IconSize.MEDIUM} />}
          title={t('paywall.premium.folder.title')}
          text={t('paywall.premium.folder.text')}
          button={
            <Button
              view="b18"
              fullWidth={false}
              onClick={newCheckout ? undefined : onShowPopup}
              href={
                newCheckout && subproductId
                  ? Url.checkoutLink(subproductId)
                  : undefined
              }
              biData={biData}
            >
              <span className={s['buttonLabel']}>
                {t('paywall.premium.folder.unlockFor')} ${dollarPrice} /{' '}
                <PointsIcon size={IconSize.TINY} disableDefaultColors />
                {pointsPrice}
              </span>
            </Button>
          }
        >
          {children}
        </BasePaywall>
      )}
      {type === 'watchers' && (
        <BasePaywall
          hasAccess={!!hasAccess}
          icon={<EyeDisc2 size={IconSize.MEDIUM} />}
          title={t('paywall.watchersOnly.folder.title')}
          text={t('paywall.watchersOnly.folder.text')}
          button={
            <WatchButton
              view="b1"
              user={contentOwner!}
              fullWidth={false}
              onToggleWatchSuccess={() => {
                window.location.href = galleryUrl!;
              }}
            />
          }
        >
          {children}
        </BasePaywall>
      )}
      {type === 'privileged' && (
        <BasePaywall
          hasAccess={!!hasAccess}
          icon={<CoreIconSmall />}
          title={t('paywall.privileged.folder.title')}
          text={t('paywall.privileged.folder.text')}
          button={
            <Button view="b18" fullWidth={false} href={URLS.CORE_MEMBERSHIP}>
              <span className={s['buttonLabel']}>
                {t('paywall.privileged.folder.buyCore')}
              </span>
            </Button>
          }
        >
          {children}
        </BasePaywall>
      )}
      {!type && children}
    </>
  );
};
FolderPaywall.displayName = 'FolderPaywall';

export default FolderPaywall;
